/**
 * Capa de compatibilidad: los tokens de la capa AU **resueltos a literales**, para vestir
 * componentes de MUI con el lenguaje del espécimen «Operar compuerta» de
 * `digital-product-guidelines`.
 *
 * **Fuente:** `digital-product-guidelines @ 9e0549f` — `src/au/tokens.css` (bloque oscuro),
 * `src/au/ui/styles/` (`dialog-alert-dialog`, `button`, `tones`, `chip-base`, `chip-badge`,
 * `number-field`, `slider`) y `src/pages/operar-compuerta/operar-compuerta.css`.
 *
 * ## Por qué existe, y cuándo se borra
 *
 * Estas pantallas están escritas en MUI y **se quedan en MUI**. Lo que hace este archivo es que
 * se parezcan a la capa sin depender de ella: cada valor es el token de AU con su `color-mix` ya
 * resuelto, y cada receta `*_SX` viste un componente de MUI con la forma que la capa le daría.
 *
 * **La llegada de `src/au/` al repo no lo mata.** Esa es la parte que la versión anterior de este
 * comentario tenía al revés: decía que el archivo se borraba «cuando la capa se porte a amaru2».
 * No es así — la capa se adopta construyendo pantallas **nuevas** sobre ella, sin mezclar, y estas
 * pantallas no se reescriben por el solo hecho de que la librería exista al lado. El archivo muere
 * cuando la pantalla que lo consume se rehaga sobre la capa; hasta entonces convive con `src/au/`
 * sin tocarlo.
 *
 * Por eso tampoco se importa nada de `src/au/` desde acá, ni siquiera cuando esté disponible:
 * mezclar los dos mundos en una misma pantalla es exactamente lo que esta decisión evita, y un
 * import sería la primera grieta.
 *
 * **Y no es `theme/operational-widgets.ts`.** Ese archivo son los tokens del mockup
 * *tableros-operacionales*, otro espécimen, con la paleta anterior a la capa: varios de sus valores
 * no coinciden con los de acá —`widgetNestedSurface` es `#222b36` contra el `#222a38` de
 * `--au-surface-2`—. Cruzarlos haría pasar por token de la capa un valor que la capa no tiene.
 *
 * ## Cómo leer los valores
 *
 * **Los `color-mix` están resueltos**, con la cuenta a la vista para poder auditarla:
 * `color-mix(in srgb, A p%, B)` sobre `transparent` es el color A con alpha `p`, y sobre un color
 * es la interpolación canal a canal.
 *
 * Lo que **sí** sale del theme de MUI y no se repite acá: `palette.divider` ya es `#919eab3d`,
 * exactamente `--au-divider` (`--au-line` #919eab al 24%), así que un borde de contenedor va con
 * `borderColor: 'divider'` y no con un literal.
 */

/* ── superficies y texto ─────────────────────────────────────────────────────────────────── */

/** `--au-surface-0` — el lienzo. En un diálogo: la cabecera. */
export const AU_SURFACE_0 = '#171c24';
const SURFACE_0 = AU_SURFACE_0;
/** `--au-surface-1` — el contenedor. En un diálogo: el popup. */
export const AU_SURFACE_1 = '#1d232e';
const SURFACE_1 = AU_SURFACE_1;
/**
 * `--au-surface-2` — lo que recibe entrada o flota. Acá: el bloque del campo y la caja del input.
 *
 * No es `background.paper` del theme (`#222b36`), que está 2 puntos de azul más arriba. La
 * diferencia no se ve en un bloque, pero el token es el token.
 */
export const AU_SURFACE_2 = '#222a38';
const SURFACE_2 = AU_SURFACE_2;
/** `--au-text`. */
export const AU_TEXT = '#edf2f7';
const TEXT = AU_TEXT;
/** `--au-text-dim`. */
export const AU_TEXT_DIM = '#a0aec0';
const TEXT_DIM = AU_TEXT_DIM;

/* ── líneas ──────────────────────────────────────────────────────────────────────────────── */

/** `--au-divider-soft` = `--au-line` (#919eab) al 16%. El filo interno de un diálogo. */
export const AU_DIVIDER_SOFT = 'rgba(145,158,171,0.16)';
const DIVIDER_SOFT = AU_DIVIDER_SOFT;
/** `--au-divider` = `--au-line` al 24%. El filo de una caja que se apoya, no de un control. */
export const AU_DIVIDER = 'rgba(145,158,171,0.24)';
/**
 * `--au-divider-strong` = `--au-line` al 34%.
 *
 * La línea que se marca más que un divider: en este espécimen, el separador vertical del recuadro
 * de posición (`.oc-position-box-divider`), que parte una caja en dos y no sólo apoya un borde.
 */
export const AU_DIVIDER_STRONG = 'rgba(145,158,171,0.34)';
/** `--au-control-border` = `--au-line` al 20%. El filo de un campo en reposo. */
const CONTROL_BORDER = 'rgba(145,158,171,0.2)';
/** `--au-control-border-hover` = `--au-primary` al 50%. */
const CONTROL_BORDER_HOVER = 'rgba(91,110,207,0.5)';
/** `--au-control-border-focus` = `--au-primary` al 70%. */
const CONTROL_BORDER_FOCUS = 'rgba(91,110,207,0.7)';
/** `--au-control-border-invalid` = `--au-error` (#c44c44) al 55%. */
const CONTROL_BORDER_INVALID = 'rgba(196,76,68,0.55)';

/* ── tonos ───────────────────────────────────────────────────────────────────────────────── */

/** `--au-primary` / `--au-primary-solid`. */
const PRIMARY = '#5b6ecf';
/** `--au-primary-solid-hover` = mezcla de `--au-primary` al 88% con negro. */
const PRIMARY_SOLID_HOVER = '#5061b6';
/** `--au-primary-text`: `--au-primary-ink` (primary 63% + text) al 85% + `--au-text`. */
const PRIMARY_TEXT = '#9fabe2';
/** `--au-primary-bg-strong` = `--au-primary` al 16%. El halo del thumb del slider. */
const PRIMARY_BG_STRONG = 'rgba(91,110,207,0.16)';
/** `--au-on-primary`. */
const ON_PRIMARY = '#ffffff';

/** `--au-success`. El verde de la capa — **no** el `#26A69A` que este feature tenía hardcodeado. */
const SUCCESS = '#4caf93';
/** `--au-success-text` = `--au-success-ink` al 85% + `--au-text`. La tinta del valor válido. */
const SUCCESS_TEXT = '#64b9a2';

/** `--au-error`. */
const ERROR = '#c44c44';
/** `--au-error-solid-hover` = mezcla de `--au-error` al 88% con negro, igual que el primario. */
const ERROR_SOLID_HOVER = '#ac433c';
/** `--au-error-ink` = `--au-error` al 64% + `--au-text`. La tinta del mensaje de error. */
const ERROR_INK = '#d38884';
/** `--au-error-text` = `--au-error-ink` al 85% + `--au-text`. La tinta del valor fuera de rango. */
const ERROR_TEXT = '#d79895';

/**
 * `--au-warning-text` = `--au-warning-ink` (= `--au-warning`, `#f4b942`) al 85% + `--au-text`.
 *
 * `--au-warning` **no tiene relleno sólido** —el ámbar no acepta blanco encima—, así que el tono
 * entra sólo por la tabla de tintes: es el cuarto tono del feed de notificaciones y no es un
 * `ActionTone`, o sea que ningún botón lo lleva. Por eso no hay un `WARNING` pelado acá.
 */
const WARNING_TEXT = '#f3c25d';

/**
 * El tono `info` de la capa. Su base (`--au-info`) es acromática —blanco en oscuro—, así que sus
 * derivados son velos blancos y no un color: es lo que lo hace un tono y no un `alpha()` suelto.
 */
const INFO_BG = 'rgba(255,255,255,0.06)'; // `--au-info-ink` al 6%
const INFO_BORDER = 'rgba(255,255,255,0.13)'; // al 13%
const INFO_BORDER_STRONG = 'rgba(255,255,255,0.19)'; // al 19%
/** `--au-info-text` apunta a `--au-text`: un chip `subtle` de info lleva la tinta normal. */
const INFO_TEXT = TEXT;

/**
 * La **tabla de tintes**: los tres valores con los que un tono pinta una superficie teñida.
 *
 * Es `tones.module.css` de la capa, que asigna `--au-tint-bg`, `--au-tint-border` y
 * `--au-tint-text` por tono y deja que cada componente los lea sin saber de qué tono se trata. Acá
 * cumple la misma función: una superficie teñida —un chip, la carta de un sensor— pide su tono y
 * no nombra un color.
 *
 * **Los nombres de los tokens no son regulares y eso es de la capa, no de acá.** `error` toma su
 * relleno y su filo de `--au-danger-bg`/`--au-danger-border`, no de `--au-error-*`; `info` es
 * acromático y sus derivados son velos blancos. Se copia como está para que un valor de este
 * archivo se pueda buscar en `tokens.css` por su nombre.
 *
 * Los porcentajes difieren por tono a propósito —12% de relleno en los tres cromáticos, pero 46%
 * de filo en `primary` contra 40% en `error` y 32% en `success`— y salen así de la capa.
 */
export type AuTone = 'info' | 'primary' | 'success' | 'warning' | 'error';

export interface AuTint {
  /** `--au-tint-bg` — el relleno teñido. */
  bg: string;
  /** `--au-tint-border` — el filo. Solo lo usa la variante `soft`. */
  border: string;
  /** `--au-tint-text` — la tinta, ya aclarada hacia `--au-text` donde el tono lo necesita. */
  text: string;
}

export const AU_TINT: Record<AuTone, AuTint> = {
  // `--au-info-*`: el acromático. Su tinta es `--au-text` pelado, no una variante teñida.
  info: { bg: INFO_BG, border: INFO_BORDER, text: INFO_TEXT },
  // `--au-primary` (#5b6ecf) al 12% y al 46%.
  primary: { bg: 'rgba(91,110,207,0.12)', border: 'rgba(91,110,207,0.46)', text: PRIMARY_TEXT },
  // `--au-success-ink` (= `--au-success`, #4caf93) al 12% y al 32%.
  success: { bg: 'rgba(76,175,147,0.12)', border: 'rgba(76,175,147,0.32)', text: SUCCESS_TEXT },
  // `--au-warning-ink` (= `--au-warning`, #f4b942) al 12% y al 32%. Acá el `-bg` sí se llama por
  // el nombre del tono: la irregularidad es sólo de `error`.
  warning: { bg: 'rgba(244,185,66,0.12)', border: 'rgba(244,185,66,0.32)', text: WARNING_TEXT },
  // `--au-danger-*`: `--au-error` (#c44c44) al 12% y al 40%.
  error: { bg: 'rgba(196,76,68,0.12)', border: 'rgba(196,76,68,0.4)', text: ERROR_TEXT },
};

/** `--au-track` = `--au-veil` (#ffffff) al 10%. La pista por la que corre el slider. */
const TRACK = 'rgba(255,255,255,0.1)';
/** `--au-hover` = `--au-veil` al 6%. Velo de hover, no fondo en reposo. */
export const AU_HOVER = 'rgba(255,255,255,0.06)';
const HOVER = AU_HOVER;
/** `--au-sunken` = `--au-veil` al 3%. Sólo huecos chicos: acá, la caja de un stepper. */
const SUNKEN = 'rgba(255,255,255,0.03)';

/* ── geometría ───────────────────────────────────────────────────────────────────────────── */

/**
 * `--au-radius-control`.
 *
 * Exportado porque lo piden consumidores que arman su propia caja en vez de usar una receta de
 * este archivo — la carta de un sensor conserva su geometría y sólo toma el color del tono.
 */
export const AU_RADIUS_CONTROL = '7px';
const RADIUS_CONTROL = AU_RADIUS_CONTROL;
/**
 * `--au-radius-card`.
 *
 * Exportado por el mismo motivo que `AU_RADIUS_CONTROL`: hay superficies que arman su propia caja
 * —la tarjeta del feed de notificaciones— y sólo toman de acá el color del tono.
 */
export const AU_RADIUS_CARD = '10px';
const RADIUS_CARD = AU_RADIUS_CARD;
/**
 * `--au-radius-tag` — el radio de un chip.
 *
 * Es menos que el de un control a propósito: a la altura de un chip (22px) el radio de control se
 * come el lado recto y la forma deja de leerse como una etiqueta. No es una píldora — la capa lo
 * dice explícitamente al fijar este valor contra el `999` que dos de sus tres definiciones de chip
 * tenían antes de unificarse.
 */
const RADIUS_TAG = '5px';
/**
 * El radio del diálogo. `.au-dialog` lo escribe literal (12px) y **no** usa `--au-radius-card`:
 * un popup lleva un punto más de radio que una card. El comentario de `.oc-frame` lo dice
 * explícitamente al copiar la geometría.
 */
const RADIUS_DIALOG = '12px';
/** `--au-shadow-dialog` = `0 24px 60px` de `--au-shadow-ink` (#000) al 55%. */
const SHADOW_DIALOG = '0 24px 60px rgba(0,0,0,0.55)';
/** `--au-backdrop` del tema oscuro, con el `blur(2px)` que `.au-backdrop` le pone encima. */
const BACKDROP = 'rgba(9,12,17,0.72)';

/** `--au-radius-pill`. */
const RADIUS_PILL = '999px';

/** `--au-scrollbar-thumb-static` — `--au-text-dim` al 65%, con el `color-mix` ya resuelto. */
const SCROLLBAR_THUMB = 'rgba(160,174,192,0.65)';

/**
 * `--au-font` — la pila tipográfica de la capa, tal cual.
 *
 * Space Grotesk ya la carga `index.html` (Google Fonts, pesos 300..700, que cubre los 400/500/600/700
 * que usa este diálogo), pero **el theme no la aplica a texto corrido**: en dev `typography.fontFamily`
 * es Barlow y sólo `h1..h6` van en Grotesk, y en producción —`baseOptions`, que entra cuando
 * `VITE_GRAPHQL_URL` no es el dev server— es Sora para todo. Declararla acá tiene el efecto de que el
 * diálogo se vea igual en los dos, en vez de depender de cuál de los dos bloques del theme esté vivo.
 */
const FONT = "'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/* ═══ recetas ════════════════════════════════════════════════════════════════════════════ */

/**
 * El velo del diálogo: `.au-backdrop`.
 *
 * Va por `slotProps.backdrop` y no por `BackdropProps`, que MUI 5 deja en pie pero marca como
 * legacy.
 */
export const AU_BACKDROP_SX = {
  bgcolor: BACKDROP,
  backdropFilter: 'blur(2px)',
} as const;

/** `.au-btn` — la caja común de las tres variantes: 34 de alto, radio de control, 12px/600. */
const AU_BTN_BASE = {
  // `minHeight` y no `height`, por lo mismo que `gateOperate/OperateButtons.tsx`: MUI no pone
  // `nowrap` en el label, así que con alto duro un label largo envuelve y se sale de la caja.
  minHeight: 34,
  borderRadius: RADIUS_CONTROL,
  px: '12px',
  py: 0,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: 1,
  border: '1px solid transparent',
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none' },
} as const;

/**
 * `.au-btn` + `.au-tone--primary` — la acción que la vista existe para hacer.
 *
 * `--au-primary` (#5b6ecf) y no `palette.primary.main` (#8395d5): el acento del theme mide 2,9:1
 * con blanco encima, insuficiente para un relleno. El mismo valor está sin exportar como
 * `floatingPrimary` en `theme/dark-theme-options.ts`.
 */
export const AU_BTN_SOLID_PRIMARY_SX = {
  ...AU_BTN_BASE,
  color: ON_PRIMARY,
  bgcolor: PRIMARY,
  borderColor: PRIMARY,
  '&:hover': { bgcolor: PRIMARY_SOLID_HOVER, borderColor: PRIMARY_SOLID_HOVER, boxShadow: 'none' },
  // `.au-btn:disabled { opacity: .4 }`: el tono se mantiene y baja de opacidad. MUI lo pinta gris.
  '&.Mui-disabled': {
    bgcolor: PRIMARY,
    borderColor: PRIMARY,
    color: ON_PRIMARY,
    opacity: 0.4,
  },
  // `loading` de MUI pasa por `disabled`, así que la regla de arriba también matchea el botón en
  // vuelo y le devuelve el label que `.MuiButton-loading` había apagado para el spinner. En vuelo
  // el botón está ocupado, no inactivo: `opacity: 1`.
  '&.MuiButton-loading': { color: 'transparent', opacity: 1 },
} as const;

/**
 * `.au-btn` + `.au-tone--error` sólido — «Detener operación».
 *
 * Es el gemelo exacto del primario, con el otro tono. Existe acá y no copiado en cada consumidor
 * porque **ya estaba copiado**: `metricsViews/.../gateOperate/OperateButtons.tsx` tenía su propio
 * `AU_SOLID` con estos mismos dos hex, escrito antes de que este archivo existiera. Un botón de
 * detener en el widget y uno en el panel de monitoreo tienen que ser el mismo botón.
 *
 * `--au-error` (#c44c44) y no `palette.error.main` (#b61f3c), que es dos puntos más oscuro.
 */
export const AU_BTN_SOLID_ERROR_SX = {
  ...AU_BTN_BASE,
  color: ON_PRIMARY,
  bgcolor: ERROR,
  borderColor: ERROR,
  '&:hover': { bgcolor: ERROR_SOLID_HOVER, borderColor: ERROR_SOLID_HOVER, boxShadow: 'none' },
  // `.au-btn:disabled { opacity: .4 }`: el tono se mantiene y baja de opacidad. Acá pesa más que en
  // el primario — «Detener operación» está deshabilitado siempre que no haya nada que detener, o
  // sea la mitad del tiempo, y el gris de MUI lo saca del tono.
  '&.Mui-disabled': {
    bgcolor: ERROR,
    borderColor: ERROR,
    color: ON_PRIMARY,
    opacity: 0.4,
  },
  // `loading` de MUI pasa por `disabled`, así que la regla de arriba también matchea el botón en
  // vuelo y le devuelve el label que `.MuiButton-loading` había apagado para el spinner. En vuelo
  // el botón está ocupado, no inactivo: `opacity: 1`.
  '&.MuiButton-loading': { color: 'transparent', opacity: 1 },
} as const;

/**
 * `.au-btn--lg` — el modificador de tamaño, para combinar con una receta de tono.
 *
 * 40 de alto, 13px (`--au-font-size-md`) y 18 de padding lateral, contra los 34/12/12 del tamaño
 * base. Es el que lleva la acción principal de una vista cuando es **la** acción: en el espécimen,
 * el «Detener operación» del monitor.
 *
 * `minHeight` y no `height`, por lo mismo que `AU_BTN_BASE`: con alto duro un label largo envuelve
 * y se sale de la caja.
 */
export const AU_BTN_LG_SX = {
  minHeight: 40,
  px: '18px',
  fontSize: 13,
} as const;

/**
 * `.au-btn--outline` + `.au-tone--info` — la acción secundaria de la capa, la que lleva «Cancelar».
 *
 * Se escribe entera porque en la capa un secundario se escribe entero: el default es el sólido.
 */
const AU_BTN_OUTLINE_INFO_SX = {
  ...AU_BTN_BASE,
  color: INFO_TEXT,
  bgcolor: 'transparent',
  borderColor: INFO_BORDER,
  '&:hover': { bgcolor: INFO_BG, borderColor: INFO_BORDER_STRONG, boxShadow: 'none' },
} as const;

/** `.au-btn--ghost.au-btn--sm` + `.au-tone--info` — «Restablecer»: 28 de alto, 11px, sin caja. */
export const AU_BTN_GHOST_INFO_SM_SX = {
  ...AU_BTN_BASE,
  minHeight: 28,
  px: '10px',
  fontSize: 11,
  color: INFO_TEXT,
  bgcolor: 'transparent',
  borderColor: 'transparent',
  '&:hover': { bgcolor: INFO_BG, borderColor: 'transparent', boxShadow: 'none' },
} as const;

/**
 * `.au-btn--soft.au-btn--sm` + `.au-tone--info` — la acción de una card: fondo teñido con filo.
 *
 * Es el escalón entre el `outline` y el sólido, y es el que el espécimen eligió para «Ver todos los
 * movimientos»: abre algo desde la card, así que tiene que verse como acción, pero no compite con
 * la primaria de la franja.
 *
 * `sm` de la capa son 28px de alto, 11px de cuerpo y 10px de padding lateral. El hover sube el filo
 * a `--au-info-border-strong` y **no** cambia el fondo — la capa marca el hover con contraste de
 * línea, no tiñendo más.
 */
export const AU_BTN_SOFT_INFO_SM_SX = {
  ...AU_BTN_BASE,
  minHeight: 28,
  px: '10px',
  gap: '6px',
  fontSize: 11,
  color: INFO_TEXT,
  bgcolor: INFO_BG,
  borderColor: INFO_BORDER,
  '&:hover': { bgcolor: INFO_BG, borderColor: INFO_BORDER_STRONG, boxShadow: 'none' },
} as const;

/**
 * La barra de scroll fina, para el `sx` de un contenedor que desborda.
 *
 * Portada de `src/au/ui/styles/scrollbar.module.css` (`.au-scrollbar-thin`) y no del thumb del
 * `ScrollArea` de la capa: ese componente mueve el scroll a un div propio y dibuja su propia
 * barra, mientras que acá el scroller es nativo. Es el mismo caso que el módulo cubre — el
 * scroller nativo de algo que desborda — y por eso toma su mismo token.
 *
 * **El color sale de `--au-scrollbar-thumb-static` (65% de `--au-text-dim`) y no del par
 * hover/reposo del 40%**: en un scroller nativo la barra es la única señal permanente de que hay
 * más contenido y no tiene estado de hover que la refuerce, así que necesita más alfa. El token
 * de la capa ya lleva esa medición.
 *
 * **El track va transparente** y no a un token de superficie: el contenedor ya tiene su fondo, y
 * un track pintado dibujaría un canal permanente en el borde aunque no haya nada que scrollear.
 *
 * **Las dos familias, a propósito.** `scrollbarWidth`/`scrollbarColor` las entienden Firefox,
 * Chrome ≥ 121 y Safari ≥ 18.2; el bloque `::-webkit-scrollbar` cubre a los Chromium y WebKit
 * anteriores, que sin él se quedan con la barra del sistema — que es justo el dibujo que esto
 * viene a reemplazar. Escribir una sola de las dos deja la mitad de los navegadores sin el
 * cambio, y sin fallar.
 *
 * No es `theme/operational-widgets.ts § widgetScrollbarSx`, que resuelve lo mismo para otro
 * espécimen con otra paleta (3px y blanco al 10%): cruzarlos haría pasar por barra de esta capa
 * una que la capa no tiene (D-001 del PR 1).
 */
export const AU_SCROLLBAR_SX = {
  scrollbarWidth: 'thin',
  scrollbarColor: `${SCROLLBAR_THUMB} transparent`,
  '&::-webkit-scrollbar': { width: '8px', height: '8px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: SCROLLBAR_THUMB,
    borderRadius: RADIUS_PILL,
  },
} as const;

/**
 * El pie paginado de un listado, para el `slotProps.pagination.sx` de `StandardListPagination`.
 *
 * **Sólo el estado encendido.** De las diferencias contra la capa, el resto entra por props del
 * componente compartido (`size`, `showFirstButton`, `siblingCount`) y ésta no tiene prop: el
 * `Pagination` de MUI marca la página actual con un gris apenas más claro que el fondo, y la capa
 * la rellena sólido. Es el precedente del `Toggle` y de las pestañas en píldora — en un conjunto
 * de pares, el que está encendido es el único que rellena—, y acá importa más que en otros lados
 * porque el número de página es el único dato que esta barra tiene que comunicar de un vistazo.
 *
 * `StandardListPagination` no se toca: tiene 23 consumidores y este diálogo es uno.
 *
 * Fuente: `digital-product-guidelines @ c7e4ea0` — `src/au/ui/pagination.tsx`, el botón de la
 * página actual en `tone="primary" variant="solid"`.
 */
export const AU_PAGINATION_SX = {
  '& .MuiPaginationItem-root.Mui-selected': {
    bgcolor: PRIMARY,
    color: ON_PRIMARY,
    fontWeight: 600,
    '&:hover': { bgcolor: PRIMARY_SOLID_HOVER },
    // MUI apaga el hover en táctil con esta media query y ahí deja el `bgcolor` base del tema, que
    // no es el nuestro: sin esto la página encendida pierde el relleno al tocarla.
    '@media (hover: none)': { bgcolor: PRIMARY },
  },
} as const;

/**
 * El chrome completo de un diálogo de la capa, para el `PaperProps.sx` del `Dialog` de MUI.
 *
 * **Se aplica desde acá y con selectores de descendencia a propósito.** El diálogo se compone con
 * `GlobalModal` → `ModalTitle`/`ModalContent`/`ModalActions`, que son componentes globales que
 * pintan medio producto: cambiarlos para alinear un diálogo tendría el blast radius de la app
 * entera. Los tres exponen la superficie justa (`dialogProps`, `sx`, `dialogActionsProps`), así
 * que la alineación cabe en la feature.
 *
 * El costo de esa decisión es que las reglas nombran clases de MUI (`.MuiDialogTitle-root`) y una
 * de ellas nombra la estructura interna de `ModalTitle` (su `Box` de título): si ese componente
 * cambia de árbol, esta regla deja de aplicar en silencio. Es el trade-off explícito frente a
 * editar un componente global — y por eso `flexDirection: row-reverse` en vez de reordenar el
 * markup: la cruz de `ModalTitle` va primera en el DOM y en la capa va a la derecha.
 *
 * Medidas de `.au-dialog` + `.au-dialog-header` + `.au-dialog-body-content` +
 * `.au-dialog-footer`, y las dos de `.oc-dialog-title`.
 */
export const AU_DIALOG_PAPER_SX = {
  bgcolor: SURFACE_1,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: RADIUS_DIALOG,
  boxShadow: SHADOW_DIALOG,
  // El `Paper` de MUI pinta su elevación con un gradiente encima del `bgcolor`; sin apagarlo la
  // superficie no es la que dice el token.
  backgroundImage: 'none',

  // ── tipografía: `--au-font` para todo el diálogo ──
  // En la capa `--au-font` se declara en el root y **todo** hereda; acá el equivalente es declararla
  // en el Paper y forzar la herencia, porque casi cada componente de MUI trae su propio
  // `font-family` de la palette de tipografía (`.MuiTypography-body1`, `.MuiButton-root`,
  // `.MuiInputBase-root`) y eso gana contra la herencia. El comodín es deliberado y del mismo
  // alcance que la custom property: se detiene en este Paper.
  //
  // `&&` y no `&`: la clase de Emotion de cada componente pesa lo mismo que `.<paper> *` —una clase
  // contra una clase más el universal, que no suma— y el empate lo resuelve el orden de inserción,
  // que acá lo gana el componente. Duplicar el selector del Paper lo deja en dos clases y cierra el
  // asunto sin `!important`. Verificado: con `&` sólo, las diez piezas de texto seguían en Barlow.
  fontFamily: FONT,
  '&& *': { fontFamily: 'inherit' },

  // ── cabecera: `.au-dialog-header` sobre `--au-surface-0` ──
  '& .MuiDialogTitle-root': {
    flexDirection: 'row-reverse',
    gap: '16px',
    px: '20px',
    py: '15px',
    bgcolor: SURFACE_0,
    borderRadius: `${RADIUS_DIALOG} ${RADIUS_DIALOG} 0 0`,
  },
  // `ModalTitle` mete 12px de padding vertical en su columna de título, que sobre los 15 de la
  // cabecera daría 27.
  '& .MuiDialogTitle-root .MuiBox-root': { py: 0 },
  // `.au-dialog-title`: 14px/700. `h6` del theme es 18px/600 y `responsiveFontSizes` lo escala.
  '& .MuiDialogTitle-root .MuiTypography-h6': {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.25,
  },
  // `.oc-dialog-title small`: 11px/500 atenuado.
  '& .MuiDialogTitle-root .MuiTypography-subtitle1': {
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.25,
    color: TEXT_DIM,
  },
  // `.au-dialog-close`: caja de 26 con 6px de radio, no el `IconButton` de 40 de MUI. `mx: 0`
  // neutraliza el `edge="start"` con que `ModalTitle` lo declara, que le pone -12px de margen: con
  // la cruz movida a la derecha ese margen se come el hueco de 16 contra el título.
  '& .MuiDialogTitle-root .MuiIconButton-root': {
    width: 26,
    height: 26,
    p: 0,
    mx: 0,
    flexShrink: 0,
    borderRadius: '6px',
    color: TEXT_DIM,
    '&:hover': { color: TEXT, bgcolor: HOVER },
  },
  '& .MuiDialogTitle-root .MuiIconButton-root .MuiSvgIcon-root': { fontSize: 18 },

  // ── cuerpo: `.au-dialog-body-content` ──
  // Los dos filos internos del diálogo son los `dividers` del `DialogContent`, que dan el
  // `border-bottom` de la cabecera y el `border-top` del pie de la capa.
  '& .MuiDialogContent-root': {
    px: '20px',
    py: '18px',
    borderColor: DIVIDER_SOFT,
    display: 'grid',
    gap: '14px',
  },
  // `.au-dialog-description`: 13px/18px atenuado, con lo resaltado en tinta normal.
  '& .MuiDialogContentText-root': {
    fontSize: 13,
    lineHeight: '18px',
    color: TEXT_DIM,
    '& b, & strong': { color: TEXT },
  },

  // ── pie: `.au-dialog-footer` ──
  '& .MuiDialogActions-root': {
    mx: 0,
    px: '20px',
    py: '14px',
    gap: '8px',
    borderTop: `1px solid ${DIVIDER_SOFT}`,
  },
  /**
   * Las dos recetas de botón del pie se aplican **acá y no por props**, y esa es la corrección de un
   * defecto real: `ConfirmationModal` reconstruye su `closeButton` quedándose sólo con `label`
   * (`ConfirmationModal.tsx`, a diferencia de `submitButton`, que sí hace `{...submitButton}`), así
   * que un `sx` pasado por esa vía se descartaba **en silencio** — typecheckea, no falla en runtime, y
   * el «Cancelar» de la confirmación se quedaba con el `outlined color="info"` de MUI en vez del
   * chrome de la capa.
   *
   * Declarándolas como reglas del pie, los dos diálogos las reciben sin depender de que un componente
   * global reenvíe props, y no hace falta tocar `ConfirmationModal` —que pinta media app— para
   * arreglarlo. `ModalActions` sólo pone `variant="outlined"` en el secundario y `variant="contained"`
   * en el primario, así que el mapeo es directo.
   */
  '& .MuiDialogActions-root .MuiButton-outlined': AU_BTN_OUTLINE_INFO_SX,
  '& .MuiDialogActions-root .MuiButton-contained': AU_BTN_SOLID_PRIMARY_SX,
} as const;

/**
 * `.au-chip` — la caja de un chip de la capa, sobre cualquier contenedor de MUI.
 *
 * **La caja mide y no pinta**, igual que `chip-base.module.css`: el color entra por `tone`, que se
 * resuelve contra `AU_TINT`. Es lo que permite que el chip de estado de una tabla y el de otra
 * vista sean la misma forma con distinto tono, en vez de dos recetas parecidas.
 *
 * Dos detalles de la capa que parecen ajustes y son decisiones, así que se copian con su razón:
 *
 * - **El `padding` vertical es asimétrico** (`4px 9px 2px`). `align-items: center` centra el *line
 *   box*, no las letras, y el line box reserva el descendente de la fuente. Como el texto de un
 *   chip casi nunca tiene descendentes —«Finalizada», «En movimiento», un número— ese espacio
 *   reservado aparece como aire abajo; el píxel de diferencia lo compensa.
 * - **La interlínea va en píxeles enteros** (14, y 12 en `sm`), no en ratio. Es lo que fija la
 *   altura de la caja —22px y 17px— con independencia del cuerpo que se le ponga adentro.
 *
 * `width: 'fit-content'` y **no** `alignSelf: 'flex-start'`: lo que hay que evitar es que el chip
 * se estire dentro de un contenedor flex en **columna**, y `alignSelf` lo evita pero de paso
 * secuestra la alineación en una **fila** —dentro de un `alignItems: center` el chip se va arriba—.
 * Un ancho definido gana igual contra el `stretch` del eje transversal sin opinar sobre el otro.
 *
 * @param tone  El tono. `subtle` es el default de la variante porque un chip aparece de a varios y
 *              a esa densidad el filo de cada uno compite con el contenido.
 */
export const auChipSx = (
  tone: AuTone,
  { variant = 'subtle', size = 'md' }: { variant?: 'subtle' | 'soft'; size?: 'md' | 'sm' } = {},
) => {
  const tint = AU_TINT[tone];
  const sm = size === 'sm';
  return {
    display: 'inline-flex',
    width: 'fit-content',
    alignItems: 'center',
    gap: sm ? '4px' : '5px',
    // `--au-font-size-xs` / `--au-font-size-2xs`.
    fontSize: sm ? 10 : 11,
    fontWeight: 600,
    lineHeight: sm ? '12px' : '14px',
    whiteSpace: 'nowrap',
    padding: sm ? '2px 7px 1px' : '4px 9px 2px',
    borderRadius: RADIUS_TAG,
    userSelect: 'none',
    color: tint.text,
    bgcolor: tint.bg,
    // El filo se declara en las dos variantes —transparente en `subtle`— para que las dos midan
    // igual y una fila que las mezcle alinee.
    border: '1px solid',
    borderColor: variant === 'soft' ? tint.border : 'transparent',
  } as const;
};

/**
 * `.oc-panel` — la card de un panel del monitor de operación.
 *
 * El cuerpo del diálogo ya es `--au-surface-1` (lo pone `AU_DIALOG_PAPER_SX`), así que un panel
 * apoyado encima se eleva un escalón, a `-2`. Es la nota de estado del 2026-09-07 de ADR-0017 del
 * lado de la capa, que corrige la tabla original del ADR: la franja apoyada dentro de un contenedor
 * no va al velo, va a la superficie de arriba.
 *
 * `.oc-panel` en el espécimen declara `--au-surface-1` y las dos áreas que lo usan lo pisan a `-2`
 * en la misma hoja; acá no hay ninguna que se quede en `-1`, así que la receta trae el valor final
 * y no el que se sobrescribe siempre.
 *
 * El radio es el de **card** (10px) y no el del diálogo (12): un popup lleva un punto más que lo
 * que vive adentro.
 */
export const AU_PANEL_SX = {
  bgcolor: SURFACE_2,
  border: '1px solid',
  borderColor: DIVIDER_SOFT,
  borderRadius: RADIUS_CARD,
  // `.oc-panel { padding: 10px 16px 12px }` — más abajo que arriba porque el título del panel ya
  // trae su propio margen inferior y el contenido termina a ras.
  padding: '10px 16px 12px',
} as const;

/**
 * Una **superficie teñida a escala de bloque**: relleno `-bg` y tinta `-text`, sin filo.
 *
 * Es la receta `subtle` del chip —la misma que llevan el estado de la tabla de movimientos y las
 * tres superficies de estado del panel—, y por eso es hermana de `auChipSx` y no su reemplazo: un
 * chip lleva radio de tag y padding de chip porque aparece de a varios en una fila; esto lleva
 * radio de control y padding de bloque porque ocupa un renglón entero. Que las dos salgan de
 * `AU_TINT` es lo que garantiza que un estado del panel y el mismo estado en la tabla se vean como
 * la misma cosa.
 *
 * **Sin filo, y sin variante `soft`.** El espécimen lo probó y lo descartó: estas superficies
 * aparecen apiladas —cuatro a seis tarjetas en el feed— y el borde de cada una suma una línea que
 * compite con el contenido.
 *
 * **Una sola tinta.** Todo lo que va adentro hereda (`color: inherit`), sin un segundo nivel
 * atenuado: `--au-text-dim` está medido contra las tres superficies, nunca contra un relleno
 * teñido, y encima de uno cae por debajo de AA. La jerarquía la llevan el cuerpo y el peso.
 *
 * @param tone    El tono. Los cinco de `AU_TINT`; el feed de notificaciones usa cuatro.
 * @param radius  `control` (7px) para una franja de una o dos líneas — la banda de estado, el valor
 *                de posición —; `card` (10px) para una tarjeta con glifo y dos renglones de texto.
 * @param padding El aire, que cambia con el alto de cada caja: `.oc-monitor-state` mide `9px 16px`,
 *                `.oc-position-box-value` `4px 16px` y `.oc-notif` `10px 18px`.
 */
export const auToneSurfaceSx = (
  tone: AuTone,
  {
    radius = 'control',
    padding = '9px 16px',
  }: { radius?: 'control' | 'card'; padding?: string } = {},
) => {
  const tint = AU_TINT[tone];
  return {
    bgcolor: tint.bg,
    color: tint.text,
    borderRadius: radius === 'card' ? RADIUS_CARD : RADIUS_CONTROL,
    padding,
  } as const;
};

/* ── «Definir operación»: las piezas propias del espécimen ───────────────────────────────── */

/** `.oc-scenario` — el asset productivo con el recorte del original. */
export const OC_SCENARIO_SX = {
  objectFit: 'cover',
  objectPosition: '0 50%',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: RADIUS_CARD,
} as const;

/** `.oc-define-row` — la fila etiqueta/valor. */
export const OC_DEFINE_ROW_SX = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  alignItems: 'center',
  gap: '12px',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: RADIUS_CONTROL,
  px: '16px',
  py: '7px',
} as const;

/** `.oc-define-row dt`. */
export const OC_DEFINE_ROW_LABEL_SX = { fontSize: 12, fontWeight: 600 } as const;

/**
 * `.oc-define-row dd` — el valor como chip `subtle` en `info`, la misma receta que el estado del
 * widget, del monitor y de la tabla. Es el único relleno teñido del diálogo: el campo editable de
 * abajo se separa por superficie, no por tono.
 */
export const OC_DEFINE_ROW_VALUE_SX = {
  fontSize: 12,
  fontWeight: 600,
  textAlign: 'center',
  bgcolor: INFO_BG,
  color: INFO_TEXT,
  borderRadius: RADIUS_CONTROL,
  px: '12px',
  py: '4px',
} as const;

/** `.oc-define-row-ago` — hereda la tinta del chip, porque el relleno es teñido. */
export const OC_DEFINE_ROW_AGO_SX = {
  fontSize: '10.5px',
  fontWeight: 400,
  color: 'inherit',
} as const;

/**
 * `.oc-define-field` — el bloque del campo sube a `--au-surface-2`: es lo único editable del
 * diálogo, y el escalón de superficie es lo que lo separa de los dos datos que solo se leen.
 */
export const OC_DEFINE_FIELD_SX = {
  bgcolor: SURFACE_2,
  border: `1px solid ${DIVIDER_SOFT}`,
  borderRadius: RADIUS_CARD,
  px: '22px',
  pt: '18px',
  pb: '20px',
  display: 'grid',
  gap: '12px',
} as const;

/** `.oc-define-field-label`. */
export const OC_DEFINE_FIELD_LABEL_SX = { fontSize: 12, fontWeight: 600 } as const;

/** `.oc-define-error` — el mensaje va en `--au-error-ink`, un paso más saturado que el valor. */
export const OC_DEFINE_ERROR_SX = {
  fontSize: '11.5px',
  fontWeight: 600,
  color: ERROR_INK,
} as const;

/** `.oc-define-scale` — los extremos del rango debajo del slider. */
export const OC_DEFINE_SCALE_SX = {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 11,
  color: TEXT_DIM,
  mt: '-6px',
} as const;

/**
 * `.au-number-group` — el campo numérico de la capa, sobre el `OutlinedInput` de MUI.
 *
 * **Es un grupo compacto de 150px, no un input estirado.** En el espécimen el `NumberField` lleva
 * `.oc-define-number { flex: 1 }`, pero eso estira su *root*: el grupo de adentro es `inline-flex`
 * y queda pegado a la izquierda con su ancho propio (medido en vivo sobre el especimen:
 * 28 + 68 + 24 + 28 + 2 de filo = 150). Lo que ocupa el resto de la fila es hueco, y por eso
 * «Restablecer» cae contra el borde derecho. La primera versión de este feature estiraba el input a
 * todo el ancho y clavaba la unidad en el extremo, que es otra composición.
 *
 * El número va a 22px/700 centrado —es el protagonista, el valor que se manda a la compuerta— y la
 * unidad a 11px/400: es contexto del número, no otro dato. Las dos se tiñen con el tono.
 *
 * `p: 0` y `overflow: hidden` en el root porque los steppers van **a ras** de la caja, como
 * `.au-number-btn`: con el padding del `OutlinedInput` quedarían flotando adentro.
 */
export const AU_NUMBER_FIELD_SX = {
  width: 'fit-content',
  '& .MuiOutlinedInput-root': {
    height: 34,
    p: 0,
    overflow: 'hidden',
    bgcolor: SURFACE_2,
    borderRadius: RADIUS_CONTROL,
    '& fieldset': { borderColor: CONTROL_BORDER },
    '&:hover fieldset': { borderColor: CONTROL_BORDER_HOVER },
    '&.Mui-focused fieldset': { borderColor: CONTROL_BORDER_FOCUS, borderWidth: '1px' },
    '&.Mui-error fieldset': { borderColor: CONTROL_BORDER_INVALID },
  },
  '& .MuiOutlinedInput-input': {
    // `.au-number-input`: 68px de ancho y `flex: none` — el número tiene su caja, y el sobrante de
    // la fila queda afuera del grupo en vez de estirarlo.
    width: 68,
    flex: 'none',
    height: 32,
    boxSizing: 'border-box',
    px: '4px',
    py: 0,
    textAlign: 'center',
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: '0.5px',
    fontVariantNumeric: 'tabular-nums',
  },
} as const;

/** `.au-number-unit` — 11px/400 pegada al número, con 8px de aire antes del stepper. */
export const AU_NUMBER_UNIT_SX = {
  fontSize: 11,
  fontWeight: 400,
  pr: '8px',
  whiteSpace: 'nowrap',
  userSelect: 'none',
} as const;

/**
 * `.au-number-btn` — los dos steppers, a ras de la caja del grupo.
 *
 * `--au-sunken` (velo blanco al 3%) es el hueco **chico** de la escala de superficies de ADR-0017,
 * y este es el único lugar del diálogo que lo lleva: los demás rellenos son de tono (`--au-info-bg`
 * en los dos rows) o de superficie (`-2` en el bloque del campo).
 */
export const AU_NUMBER_STEPPER_SX = {
  width: 28,
  height: 32,
  p: 0,
  borderRadius: 0,
  flexShrink: 0,
  color: TEXT_DIM,
  bgcolor: SUNKEN,
  '&:hover': { color: TEXT, bgcolor: HOVER },
  '&.Mui-disabled': { color: TEXT_DIM, opacity: 0.35 },
  '& .MuiSvgIcon-root': { fontSize: 12 },
} as const;

/** El estado visual del campo, con la precedencia `error > dirty > idle` de `validate.ts`. */
export type PositionTone = 'idle' | 'dirty' | 'error';

/**
 * La tinta del valor por tono: `.oc-define-field[data-tone=…] .oc-define-number input`.
 *
 * `idle` no declara color —hereda `--au-text`—, y los dos estados teñidos usan el `-text` de su
 * tono, que es la variante aclarada hacia el texto: el `-ink` pelado no tiene contraste suficiente
 * sobre `--au-surface-2`.
 */
export const POSITION_TONE_COLOR: Record<PositionTone, string> = {
  idle: TEXT,
  dirty: SUCCESS_TEXT,
  error: ERROR_TEXT,
};

/**
 * El color del indicador del slider por tono: `.oc-define-slider` reasigna `--au-primary`.
 *
 * En reposo el indicador es **indigo**, no blanco: es la pista del valor y el valor sin cambios
 * sigue siendo un valor. Los tonos usan la base cromática (`--au-success` / `--au-error`) y no su
 * `-text`, porque acá el color es relleno y no tinta.
 */
export const SLIDER_TONE_COLOR: Record<PositionTone, string> = {
  idle: PRIMARY,
  dirty: SUCCESS,
  error: ERROR,
};

/**
 * `.au-slider` — pista de 4px sobre `--au-track`, thumb de 14 en `--au-text` con halo de
 * `--au-primary-bg-strong` al tocarlo. El anillo de foco lleva el lienzo debajo para separarlo de
 * la pista, igual que `.au-slider-thumb:focus-visible`.
 */
export const AU_SLIDER_SX = {
  height: 4,
  py: '7px', // los 18px de alto de `.au-slider-control` alrededor de una pista de 4
  '& .MuiSlider-rail': { bgcolor: TRACK, opacity: 1 },
  '& .MuiSlider-track': { border: 'none' },
  '& .MuiSlider-thumb': {
    width: 14,
    height: 14,
    bgcolor: TEXT,
    border: 'none',
    '&:hover, &.Mui-active': { boxShadow: `0 0 0 5px ${PRIMARY_BG_STRONG}` },
    '&.Mui-focusVisible': {
      boxShadow: `0 0 0 1px ${SURFACE_0}, 0 0 0 3px ${PRIMARY_TEXT}`,
    },
    '&::before': { display: 'none' },
  },
} as const;

/**
 * El verde con el que la confirmación resalta la posición de destino.
 *
 * `--au-success` y no el `#26A69A` que este archivo tenía: en el especimen la confirmación pinta
 * ese fragmento con `style={{ color: 'var(--au-success)' }}`, la base del tono, porque va en
 * negrita sobre texto atenuado y no necesita la variante aclarada.
 */
export const AU_SUCCESS = SUCCESS;

/**
 * El rojo de la capa como **marca gráfica** —la base pelada del tono, no su tinta—: la franja de la
 * barra de progreso cuando la operación terminó con error. Para una superficie con texto encima va
 * `auToneSurfaceSx('error')`, que lleva el par `-bg`/`-text` medido a 4,5:1.
 */
export const AU_ERROR = ERROR;
